import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiUrl, DEFAULT_BRAND_LOGO } from '../../config/api';
import { fetchBrandImageUrl } from '../../services/brandingService';
import {
  parseFooterContents,
  resolveFooterAbout,
  resolveFooterAddress,
} from '../../utils/footerContent';
import emailjs from "@emailjs/browser";
import { useTranslation } from "react-i18next";
import axios from "axios";
import "../../i18n";
import useBusinessStatus from "./BusinessStatus";
import "../../assets/css/style.css";
import "./Footer.css";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [officeEmail, setOfficeEmail] = useState("");
  const [facebookLink, setFacebookLink] = useState("");
  const [whatsappLink, setWhatsappLink] = useState("");
  const [wechatLink, setWechatLink] = useState("");
  const [callLink, setCallLink] = useState("");
  const form = useRef();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { currentTime, status } = useBusinessStatus();
  const [logoUrl, setLogoUrl] = useState("");
  const [footerContents, setFooterContents] = useState({
    addressLao: "",
    addressEnglish: "",
    footerLao: "",
    footerEnglish: "",
  });

  const addressText = useMemo(
    () =>
      resolveFooterAddress(footerContents, i18n.language, t("AddressInfo")),
    [footerContents, i18n.language, t]
  );

  const aboutFooterText = useMemo(
    () =>
      resolveFooterAbout(footerContents, i18n.language, t("AboutTitleFooter")),
    [footerContents, i18n.language, t]
  );

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);

    emailjs
      .sendForm(
        "service_66hwnto",
        "template_wl7tkzh",
        form.current,
        "ahFicaJT9aVdFUKVW"
      )
      .then(
        (result) => {
          setFormData({
            email: "",
          });
          setError(null);
        },
        (error) => {
          console.error("Email sending error:", error.text);
          setError("Failed to send email. Please try again.");
        }
      )
      .finally(() => setLoading(false));
  };

  const [formData, setFormData] = useState({
    email: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const fetchLogo = useCallback(async () => {
    const logo = await fetchBrandImageUrl('logo');
    if (logo) {
      setLogoUrl(logo);
    }
  }, []);

  // Determine the CSS class based on the status
  const statusClass =
    status === t("open") ? "alternative-status-open" : "status-closed";

  const handleSubmit = (e) => {
    e.preventDefault();
    sendEmail(e);
  };
  const fetchEmailSettings = useCallback(async () => {
    try {
      const response = await axios.get(
        apiUrl('/email-updates')
      );
      if (response.data) {
        response.data.forEach((setting) => {
          if (setting.type === "office") setOfficeEmail(setting.value);
        });
      }
    } catch (error) {
      console.error("Error fetching email settings:", error);
    }
  }, []);

  const initFooterContents = useCallback(async () => {
    try {
      const response = await axios.get(apiUrl("/other-contents"));
      const parsedContents = parseFooterContents(response.data);
      setFooterContents(parsedContents);
    } catch (error) {
      console.error("Error fetching footer contents:", error);
    }
  }, []);

  const fetchSocialLinks = useCallback(async () => {
    try {
      const response = await axios.get(
        apiUrl('/social-links')
      );
      if (response.data) {
        response.data.forEach((setting) => {
          if (setting.type === "facebook") setFacebookLink(setting.value);
          if (setting.type === "whatsapp") setWhatsappLink(setting.value);
          if (setting.type === "wechat") setWechatLink(setting.value);
          if (setting.type === "call") setCallLink(setting.value);
        });
      }
    } catch (error) {
      console.error("Error fetching social links:", error);
    }
  }, []);

  useEffect(() => {
    fetchEmailSettings();
    fetchLogo();
    initFooterContents();
    fetchSocialLinks();
  }, [fetchEmailSettings, fetchLogo, fetchSocialLinks, initFooterContents]);

  return (
    <footer className="footer">
      <div className="footer_above">
        <div className="container">
          <div className="row">
            <div className="col-lg-4 col-md-6 col-sm-12">
              <div className="footer_widget footer_widget_padding">
                <h4 className="widget_title">
                  {t("About")} {t("TPS")}
                </h4>
                <p>{aboutFooterText}</p>
                <div className="subscribe">
                  <form
                    ref={form}
                    onSubmit={handleSubmit}
                    className="form-inline"
                  >
                    <div className="input-group mb-2 mr-sm-2">
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        placeholder={t("EmailAddress")}
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    <button type="submit" className="btn" disabled={loading}>
                      {loading ? t("Sending...") : t("Subscribe")}
                    </button>
                    {error && <p className="form_error">{error}</p>}
                  </form>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-6 col-lg-2">
              <div className="footer_widget footer_links footer_links--services">
                <h4 className="widget_title">{t("OurServices")}</h4>
                <div className="footer_nav">
                  <ul className="footer_menu">
                    <li className="menu-item">
                      <a href="/construction">{t("Constructions")}</a>
                    </li>
                    <li className="menu-item">
                      <a href="/ImportExport">{t("ImportExport")}</a>
                    </li>
                    <li className="menu-item">
                      <a href="/gaslpg">{t("GasLPG")}</a>
                    </li>
                    <li className="menu-item">
                      <a href="/factoryOverview">{t("CrushingPlant")}</a>
                    </li>
                    <li className="menu-item">
                      <a href="/apartment">{t("Apartment")}</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-6 col-lg-2">
              <div className="footer_widget footer_links footer_links--info">
                <h4 className="widget_title">{t("MoreInfo")}</h4>
                <div className="footer_nav">
                  <ul className="footer_menu">
                    <li className="menu-item">
                      <a href="/about">{t("About")}</a>
                    </li>
                    <li className="menu-item">
                      <a href="/project">{t("Projects")}</a>
                    </li>
                    <li className="menu-item">
                      <a href="/blog-1">{t("News")}</a>
                    </li>
                    <li className="menu-item">
                      <a href="/contact">{t("Contact")}</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12">
              <div className="footer_widget">
                <h4 className="widget_title">{t("Address")}</h4>
                <ul>
                  <li>
                    <i className="fa fa-map-marker" aria-hidden="true"></i>
                    <span>{addressText}</span>
                  </li>
                  <li>
                    <i className="fa fa-envelope" aria-hidden="true"></i>
                    <span>{officeEmail}</span>
                  </li>
                  <li>
                    <i className="fa fa-clock-o" aria-hidden="true"></i>
                    <div className="footer-hours-block">
                      <span>{t("MondayFriday")} (8.30am - 5.30pm)</span>
                      <strong className="footer-status-line">
                        {t("status")} :{" "}
                        <span className={statusClass}>{t(status)}</span>
                      </strong>
                      <span
                        className="footer-live-clock alternative-status-open"
                        aria-live="polite"
                      >
                        {currentTime.toLocaleTimeString()}
                      </span>
                    </div>
                  </li>
                </ul>
                <div className="side_footer_social">
                  <ul className="bottom_social">
                    <li className="facebook">
                      <a
                        href={facebookLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="ion-social-facebook"></i>
                      </a>
                    </li>
                    <li className="whatsapp">
                      <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="ionicons ion-social-whatsapp-outline"></i>
                      </a>
                    </li>
                    <li className="dribbble">
                      <a
                        href={wechatLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="ionicons ion-chatbubbles"></i>
                      </a>
                    </li>
                    <li className="facebook">
                      <a href={`tel:${callLink}`}>
                        <i className="ionicons ion-android-call"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="footer_bottom">
        <div className="container">
          <div className="footer_bottom_inner footer_bottom_inner--compact">
            <div className="footer_bottom__top">
              <a href="/" className="footer_bottom__logo">
                <img src={logoUrl || DEFAULT_BRAND_LOGO} alt="TPS logo" />
              </a>
              <div className="footer_bottom__actions">
                <a
                  href="https://admin.tps-lao.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer_bottom__login login"
                >
                  {t("Login")}
                </a>
                <a href="/#" className="footer_bottom__totop" aria-label="Back to top">
                  <i className="ion-ios-arrow-up" aria-hidden="true"></i>
                </a>
              </div>
            </div>
            <div className="footer_bottom__meta">
              <p className="footer_bottom__copyright">
                &copy;{currentYear}{" "}
                <a
                  href="https://aitthisone.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  TPS-Lao
                </a>{" "}
                {t("AllRightsReserved", { defaultValue: "All rights reserved." })}
              </p>
              <p className="footer_bottom__company">
                <a href="/#">{t("TPS")}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
